import React, { useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Wifi, Phone, MessageSquare, Clock, Copy, CheckCircle2, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../auth/AuthContext";

// Display + management for a single eSIM activation. Renders the QR code,
// install steps, and a "Mark as installed" CTA. Used in both the post-
// booking confirmation page and the My eSIMs profile view.
export default function EsimCard({ esim, onUpdate }) {
  const [busy, setBusy] = useState(false);
  if (!esim) return null;

  const plan = esim.plan || {};
  const status = esim.status || "ready";
  const providerLabel =
    esim.provider === "airalo" ? "Airalo (live)" :
    esim.provider === "mock_zcars" ? "Mock — not Airalo" :
    esim.provider === "mock_fallback" ? "Z-Cars (pending)" :
    "Z-Cars (demo)";
  const expires = esim.expires_at ? new Date(esim.expires_at) : null;
  const expiresLabel = expires
    ? expires.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : "—";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(esim.qr_payload || "");
      toast.success("Activation code copied");
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  const markInstalled = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/esim/${esim.activation_id}/mark-installed`, {}, { withCredentials: true });
      toast.success("Marked as installed");
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    } finally { setBusy(false); }
  };

  return (
    <div className="zc-card overflow-hidden" data-testid={`esim-${esim.activation_id}`}>
      <div className="px-5 py-4 flex items-center gap-3 border-b border-[var(--zc-border)]" style={{ background: "var(--zc-secondary-soft)" }}>
        <div className="w-9 h-9 grid place-items-center rounded-lg text-white" style={{ background: "var(--zc-secondary)" }}>
          <Smartphone size={16} />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-sm">{plan.name || "Travel Pack"}</div>
          <div className="zc-hint">{plan.region || "Global"}</div>
        </div>
        <StatusPill status={status} />
      </div>

      <div className="p-5 grid sm:grid-cols-[200px,1fr] gap-6 items-start">
        <div className="flex flex-col items-center">
          <div className="bg-white p-3 rounded-xl border border-[var(--zc-border)]" data-testid={`esim-qr-${esim.activation_id}`}>
            <QRCodeSVG value={esim.qr_payload || ""} size={170} level="M" includeMargin={false} />
          </div>
          <button
            onClick={copy}
            className="mt-3 inline-flex items-center gap-1.5 zc-hint hover:text-[var(--zc-text)]"
            data-testid={`esim-copy-${esim.activation_id}`}
          >
            <Copy size={11} /> Copy activation code
          </button>
        </div>

        <div>
          <div className="grid grid-cols-3 gap-3">
            <Stat icon={<Wifi size={13} />} label="Data" value={`${(plan.data_mb || 0) >= 1024 ? Math.round((plan.data_mb || 0)/1024)+" GB" : (plan.data_mb||0)+" MB"}`} />
            <Stat icon={<Phone size={13} />} label="Voice" value={`${plan.voice_minutes || 0} min`} />
            <Stat icon={<MessageSquare size={13} />} label="SMS" value={`${plan.sms_count || 0}`} />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <Stat icon={<Clock size={13} />} label="Valid until" value={expiresLabel} />
            <Stat icon={<Globe2 size={13} />} label="Provider" value={providerLabel} />
          </div>

          {esim.direct_apple_installation_url && (
            <a
              href={esim.direct_apple_installation_url}
              className="mt-3 inline-flex text-xs text-[var(--zc-secondary)] hover:underline"
              data-testid={`esim-apple-install-${esim.activation_id}`}
            >
              Install on iPhone (iOS 17.4+)
            </a>
          )}

          <div className="mt-5 pt-5 border-t border-[var(--zc-border)]">
            <div className="eyebrow mb-2">How to install</div>
            <ol className="text-xs text-[var(--zc-text-2)] space-y-1.5 leading-relaxed pl-4 list-decimal">
              <li><b className="text-[var(--zc-text)]">iPhone</b>: Settings → Cellular → Add eSIM → Use QR code → scan above.</li>
              <li><b className="text-[var(--zc-text)]">Android</b>: Settings → Network → Mobile network → Add carrier → scan QR.</li>
              <li>Toggle the new line on once you've landed. Roaming activates automatically.</li>
            </ol>
          </div>

          <div className="zc-hint mt-4 space-y-1">
            ICCID <span className="font-mono">{esim.iccid}</span>
            {esim.provider === "airalo" && esim.airalo_order_id && (
              <div>Airalo order <span className="font-mono">{esim.airalo_order_id}</span>{esim.airalo_order_code ? ` · ${esim.airalo_order_code}` : ""}</div>
            )}
            {esim.provider === "mock_zcars" && (
              <div style={{ color: "var(--zc-warn,#f59e0b)" }}>Demo QR only — configure Airalo credentials for real eSIM provisioning.</div>
            )}
          </div>

          {status === "ready" && (
            <button
              onClick={markInstalled}
              disabled={busy}
              className="mt-4 inline-flex items-center gap-1.5 px-4 h-9 zc-btn-ghost text-xs disabled:opacity-50"
              data-testid={`esim-installed-${esim.activation_id}`}
            >
              <CheckCircle2 size={13} /> I've installed it
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    ready: { label: "Ready to install", bg: "rgba(245,158,11,0.14)", fg: "var(--zc-warn)" },
    installed: { label: "Installed", bg: "rgba(16,185,129,0.14)", fg: "var(--zc-success)" },
    used: { label: "Used", bg: "var(--zc-bg-3)", fg: "var(--zc-text-3)" },
    expired: { label: "Expired", bg: "var(--zc-bg-3)", fg: "var(--zc-text-3)" },
  };
  const s = map[status] || map.ready;
  return (
    <span className="zc-badge px-2 py-1" style={{ background: s.bg, color: s.fg }}>
      {s.label}
    </span>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div>
      <div className="eyebrow flex items-center gap-1.5">{icon} {label}</div>
      <div className="font-semibold text-sm mt-1">{value}</div>
    </div>
  );
}
